import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ApplianceDetailsStore, ApplianceDetailsState } from '../state/appliance-details.store';
import { ApplianceDetailsQuery } from '../state/appliance-details.query';
import { ApplianceTypeEnums } from '../models/appliance-type.enum';
import { ApplianceDetailsModel } from '@shared/models/rx-models/interfaces/appliance-details-model';
import { IdName } from '@shared/models/id-name';

/**
 * Initialises ApplianceDetailsStore from shell RX exactly once when the app starts —
 * analogous to NotesService.setNotesArray().
 *
 * Components call loadFromStore() on init and always get the current value,
 * even after hide/show cycles — no getLoadedRx() in components needed.
 */
@Injectable({ providedIn: 'root' })
export class ApplianceDetailsInitService {
	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly store: ApplianceDetailsStore,
		private readonly query: ApplianceDetailsQuery
	) {}

	/** Reads ApplianceDetails from the loaded RX once and writes into ApplianceDetailsStore. */
	setApplianceDetails(): Observable<ApplianceDetailsState> {
		return combineLatest([this.shellQuery.applianceDetails$, this.query.applianceTypes$]).pipe(
			filter((latest): latest is [ApplianceDetailsModel, IdName<number>[]] => !!latest[0]),
			take(1),
			map(([applianceDetails, applianceTypes]) => ({
				isUpperChecked: applianceDetails.UpperJaw,
				isLowerChecked: applianceDetails.LowerJaw,
				applianceType: applianceTypes.find(t => t.Id === applianceDetails.ApplianceType) ?? {
					Id: ApplianceTypeEnums.Empty,
					Name: ''
				}
			})),
			tap(state => this.store.update(state))
		);
	}
}
